import React, { FC, useMemo } from 'react';
import {
  BooleanInput,
  DateTimeInput,
  SelectInput,
  SimpleForm,
  SimpleFormProps,
  TextInput,
  required,
  useRecordContext,
} from 'react-admin';
import {
  ANNOUNCEMENT_CHANNEL_ID,
  SCHEDULED_MESSAGE_CHANNELS,
  formatTimeForInput,
  getChannelLabel,
  parseTimeAsUtc,
} from '../../../Shared/ScheduledMessage.types';
import FileUploadButton from '../FileUploadButton/FileUploadButton';

const ScheduledMessageForm: FC<Omit<SimpleFormProps, 'children'>> = (props) => {
  const record = useRecordContext();

  // Inject the record's channel into the dropdown if we don't recognize it,
  // so editing a row stored against e.g. an older command channel still shows
  // the current value selected instead of an empty SelectInput.
  const channelChoices = useMemo(() => {
    const channelId = record?.channelId as string | undefined;
    if (
      channelId &&
      !SCHEDULED_MESSAGE_CHANNELS.some((channel) => channel.id === channelId)
    ) {
      return [
        ...SCHEDULED_MESSAGE_CHANNELS,
        { id: channelId, name: getChannelLabel(channelId) },
      ];
    }
    return SCHEDULED_MESSAGE_CHANNELS;
  }, [record?.channelId]);

  return (
    <SimpleForm {...props}>
      <DateTimeInput
        source="time"
        label="Send time (UTC)"
        validate={required()}
        format={formatTimeForInput}
        parse={parseTimeAsUtc}
        defaultValue={new Date().toISOString()}
        helperText="Time is in UTC. The bot posts the message once this time is reached."
      />
      <SelectInput
        source="channelId"
        label="Channel"
        choices={channelChoices}
        defaultValue={ANNOUNCEMENT_CHANNEL_ID}
        validate={required()}
        fullWidth
      />
      <TextInput
        source="content"
        label="Message"
        validate={required()}
        multiline
        minRows={5}
        fullWidth
        helperText="Plain text, or use <Title>...</Title><Message>...</Message> for an embed."
      />
      <TextInput
        source="files"
        label="Image / file URLs"
        fullWidth
        helperText={
          'Optional. Comma-separated URLs. Sent as Discord attachments — ' +
          'keep the message as plain text (no <Title> embed) for it to show.'
        }
      />
      <FileUploadButton />
      <BooleanInput
        source="active"
        label="Active (uncheck to cancel)"
        defaultValue={true}
      />
    </SimpleForm>
  );
};

export default ScheduledMessageForm;
